import {useFrame} from '@react-three/fiber';
import {useEffect, useState} from 'react';
import {TrainingTarget} from '../../components/app/TrainingTarget';
import {
  VR_POSITIONS,
  createPeripheralPositions,
  createTargetPosition,
  getDistanceValue,
  getRangeValue,
  getSpeedValue,
} from '../../constants/vrPositions';
import {ballPositionStore, useXRController} from '../../hooks/useXRController';
import {useStore} from '../../store/useStore';
import {ExerciseSettings} from '../../types/exercises';
import {logger} from '../../utils/logger';

export const PursuitExercise: React.FC<ExerciseSettings> = ({
  eyeToTrain = 'both',
  isRedGreen = false,
  isMFBF = false,
  isOverlay = false,
  leftEyeColor,
  rightEyeColor,
  leftEyeOverlay,
  rightEyeOverlay,
  speed = 7,
  range = 'medium',
  distance = 'middle',
  ballPosition,
  onEndExercise,
}) => {
  const initialDistance = getDistanceValue(distance);
  const initialPosition = createTargetPosition(0, VR_POSITIONS.EXERCISE.TARGET_HEIGHT, initialDistance);

  const [targetPosition, setTargetPosition] = useState(initialPosition);
  const [isInitialized, setIsInitialized] = useState(false);
  const [time, setTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const targetDistance = getDistanceValue(distance);

  const toggleTimerPause = useStore(state => state.toggleTimerPause);

  const {isInXR, handlePositionChange} = useXRController();

  const onMenuToggle = (isOpen: boolean) => {
    setIsPaused(isOpen);
    toggleTimerPause(isOpen);
    logger.info(`PursuitExercise: Menu toggled, global pause set to ${isOpen}`);
  };

  const handleEndExercise = () => {
    logger.info('PursuitExercise: End exercise requested');
    if (onEndExercise) {
      onEndExercise();
    }
  };

  const [leftPeripheral, rightPeripheral] = createPeripheralPositions(initialDistance);

  useEffect(() => {
    logger.info('PursuitExercise mounted');
    setIsInitialized(true);
    return () => {
      logger.info('PursuitExercise unmounted');
    };
  }, []);

  // Initialize ball position from saved settings
  useEffect(() => {
    if (ballPosition) {
      logger.info('Initializing ball position from saved settings:', ballPosition);
      ballPositionStore.setOffset(ballPosition);
    }
  }, [ballPosition]);

  useFrame((_, delta) => {
    if (!isInitialized || isPaused) {
      return;
    }

    setTime(prev => prev + delta);

    // Calculate movement values using current props
    const movementSpeed = getSpeedValue(speed);
    const movementRange = getRangeValue(range);

    const x = Math.cos(time * movementSpeed) * movementRange;
    const newPosition = createTargetPosition(x, VR_POSITIONS.EXERCISE.TARGET_HEIGHT, targetDistance);
    setTargetPosition(newPosition);
  });

  return (
    <group>
      <TrainingTarget
        position={targetPosition}
        showPeripheral={false}
        eyeToTrain={eyeToTrain}
        isRedGreen={isRedGreen}
        isMFBF={isMFBF}
        isOverlay={isOverlay}
        leftEyeColor={leftEyeColor}
        rightEyeColor={rightEyeColor}
        leftEyeOverlay={leftEyeOverlay}
        rightEyeOverlay={rightEyeOverlay}
        inXRSession={isInXR}
        onPositionChange={handlePositionChange}
        onMenuToggle={onMenuToggle}
        onEndExercise={handleEndExercise}
      />

      {isMFBF && (
        <>
          <TrainingTarget
            position={leftPeripheral}
            scale={0.5}
            showPeripheral={true}
            eyeToTrain={eyeToTrain}
            isRedGreen={isRedGreen}
            isMFBF={isMFBF}
            isOverlay={isOverlay}
            inXRSession={isInXR}
          />
          <TrainingTarget
            position={rightPeripheral}
            scale={0.5}
            showPeripheral={true}
            eyeToTrain={eyeToTrain}
            isRedGreen={isRedGreen}
            isMFBF={isMFBF}
            isOverlay={isOverlay}
            inXRSession={isInXR}
          />
        </>
      )}
    </group>
  );
};
